<template>
  <div class="max-w-5xl mx-auto">
    <!-- ── Sticky header ── -->
    <div ref="stickyHeaderRef" class="relative sticky z-10 bg-white px-4 pt-4 pb-0 space-y-2 sm:space-y-4 shadow-sm" :style="{ top: (props.stickyHeaderOffset ?? 0) + 'px' }">
    <!-- Controls -->
    <div class="flex items-center gap-2">
      <input v-model="searchQuery" placeholder="Optional term(s) in argument" label="Enter terms for search" aria-describedby="q-help" @keyup.enter="onSearch"
        class="flex-1 min-w-0 rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

      <!-- Search button: optional terms bar -->
      <button
        v-if="searchButtonPos === 'lower'"
        @click="onSearch"
        :disabled="activeLoading"
        class="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white
              hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ activeLoading ? 'Searching…' : 'Search' }}
      </button>

      <!-- Info button: optional terms bar (right) -->
      <button
        v-if="infoButtonPos === 'upper-right'"
        @click="showInfo = !showInfo"
        class="ml-auto shrink-0 rounded-full border border-gray-300 bg-white w-8 h-8 flex items-center justify-center text-xs font-bold text-gray-500 hover:bg-gray-50"
        :class="{ 'bg-blue-50 border-blue-300 text-blue-600': showInfo }"
        :aria-expanded="showInfo ? 'true' : 'false'"
        title="Show help"
      >?</button>
    </div>

    <!-- Dev options row -->
    <div v-if="isDevMode && devOptionsVisible" class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
      <span>Development options:</span>
      <label class="flex items-center gap-1 cursor-pointer select-none">
        <input type="checkbox" v-model="flatInfo" class="accent-gray-400" />
        flat info boxes
      </label>
      <label class="flex items-center gap-1 cursor-pointer select-none">
        <input type="checkbox" v-model="stickyClaimBarModel" class="accent-gray-400" />
        sticky claim bar
      </label>
      <span>Sort By position:</span>
      <select v-model="sortPosition" class="rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-400">
        <option value="views-left">Views row (left)</option>
        <option value="views-right">Views row (right)</option>
        <option value="stats-left">Stats row (left)</option>
        <option value="stats-right">Stats row (right)</option>
      </select>
      <span>Stance position:</span>
      <select v-model="stancePosition" class="rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-400">
        <option value="views-left">Views row (left)</option>
        <option value="views-right">Views row (right)</option>
        <option value="stats-left">Stats row (left)</option>
        <option value="stats-right">Stats row (right)</option>
      </select>
      <span>Search button:</span>
      <select v-model="searchButtonPos" class="rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-400">
        <option value="upper">Claim bar</option>
        <option value="lower">Search bar</option>
      </select>
      <span>Info button:</span>
      <select v-model="infoButtonPos" class="rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-400">
        <option value="controls">Claim bar</option>
        <option value="upper-right">Optional terms bar (right)</option>
        <option value="tabs">Views row (right)</option>
        <option value="floating">Floating (bottom-right)</option>
      </select>
      <button @click="devOptionsVisible = false" class="ml-auto text-gray-300 hover:text-gray-500">hide</button>
    </div>

    <div
      v-if="showInfo"
      class="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2"
    >
      <p>Search for a <strong>claim</strong> (e.g. "We should fight urbanization"), then browse or filter the <strong>arguments</strong> collected for that claim — grouped by stance, ranked by quality or relevance, and optionally clustered into <strong>key points</strong>.</p>
      <p class="font-semibold pt-1">Controls</p>
      <ul class="space-y-1 list-none">
        <li><strong>Claim field</strong> — Type to search for a claim and select one from the suggestions. A claim must be selected before results can be loaded.</li>
        <li><strong>Optional terms field</strong> — Narrow results by entering keywords that must appear in the argument text. Leave empty to see all arguments for the claim.</li>
        <li><strong>Search</strong> — Runs the search with the current settings.</li>
        <li><strong>Stance</strong> — Filter by argument direction: <span class="text-blue-950">Pro &amp; Con</span> shows both sides in two columns, <span class="text-blue-950">Pro</span> or <span class="text-blue-950">Con</span> shows only one side.</li>
        <li><strong>Sort By</strong> — Only active when optional terms are used. Results can be sorted by <span class="text-blue-950">Argument Quality</span> or <span class="text-blue-950">Search Relevance</span>.</li>
      </ul>
      <p class="font-semibold pt-1">Views</p>
      <ul class="space-y-1 list-none">
        <li><strong>Arguments</strong> — Shows individual argumentative text segments. Each card displays the argument text, its quality score, relevance score, and the key point it belongs to. Clicking a key point navigates to that key point in the Key Points view.</li>
        <li><strong>Key Points</strong> — Groups arguments by their key point, a short summary of the argument's core claim. Click a key point to expand it and browse its arguments. Greyed-out key points had matching arguments in the base set but none for the current search terms.</li>
      </ul>
    </div>

    <!-- Mobile: Sort/Stance row directly under the search input -->
    <div class="flex sm:hidden items-center gap-2 text-xs">
      <span class="text-gray-500" :class="{ 'opacity-40': !searchedWithTerms }">Sort By:</span>
      <select v-model="searchSort" :disabled="!searchedWithTerms" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
               focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
        <option value="quality">Argument Quality</option>
        <option value="relevance">Search Relevance</option>
      </select>
      <span class="text-gray-500 ml-auto">Stance:</span>
      <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
               focus:outline-none focus:ring-2 focus:ring-blue-500">
        <option value="all">Pro &amp; Con</option>
        <option value="pro">Pro</option>
        <option value="con">Con</option>
      </select>
    </div>

    <!-- View tabs -->
    <div class="flex flex-wrap items-center gap-5 sm:gap-1 border-b border-gray-200">
      <button
        v-if="showKeypointsTab"
        @click="switchView('keypoints')"
        class="sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
        :class="viewMode === 'keypoints'
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        Key Points
      </button>
      <button
        @click="switchView('arguments')"
        class="sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
        :class="viewMode === 'arguments'
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        Arguments
      </button>

      <!-- Mobile: inline stats on the right of the tab row -->
      <span v-if="argResp && viewMode === 'arguments'" class="sm:hidden ml-auto text-xs text-gray-500 truncate">
        {{ argResp.total }} args<template v-if="searchStance === 'all' && argProResp && argConResp"> · <span class="text-purple-700">{{ argProResp.total ?? 0 }} pro</span> · <span class="text-[#3A91A5]">{{ argConResp.total ?? 0 }} con</span></template>
      </span>
      <span v-else-if="kpResp && viewMode === 'keypoints'" class="sm:hidden ml-auto text-xs text-gray-500 truncate">
        {{ kpResp.total_arguments ?? 0 }} args<template v-if="searchStance === 'all'"> · <span class="text-purple-700">{{ kpProArgCount }} pro</span> · <span class="text-[#3A91A5]">{{ kpConArgCount }} con</span></template>
      </span>

      <!-- Sort By: left side of tab bar (desktop only positions) -->
      <template v-if="sortPosition === 'views-left'">
        <span class="hidden sm:inline ml-2 text-xs text-gray-500" :class="{ 'opacity-40': !searchedWithTerms }">Sort By:</span>
        <select v-model="searchSort" :disabled="!searchedWithTerms" class="hidden sm:block rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
          <option value="quality">Argument Quality</option>
          <option value="relevance">Search Relevance</option>
        </select>
      </template>

      <!-- Stance: left side of tab bar -->
      <template v-if="stancePosition === 'views-left'">
        <span class="hidden sm:inline ml-2 text-xs text-gray-500">Stance:</span>
        <select v-model="searchStance" class="hidden sm:block rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">Pro &amp; Con</option>
          <option value="pro">Pro</option>
          <option value="con">Con</option>
        </select>
      </template>

      <!-- Right-side group: desktop only -->
      <div
        v-if="sortPosition === 'views-right' || stancePosition === 'views-right' || infoButtonPos === 'tabs'"
        class="hidden sm:flex items-center gap-1 sm:flex-1 sm:justify-end"
      >
        <template v-if="sortPosition === 'views-right'">
          <span class="text-xs text-gray-500" :class="{ 'opacity-40': !searchedWithTerms }">Sort By:</span>
          <select v-model="searchSort" :disabled="!searchedWithTerms" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
            <option value="quality">Argument Quality</option>
            <option value="relevance">Search Relevance</option>
          </select>
        </template>

        <template v-if="stancePosition === 'views-right'">
          <span class="text-xs text-gray-500 ml-2">Stance:</span>
          <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Pro &amp; Con</option>
            <option value="pro">Pro</option>
            <option value="con">Con</option>
          </select>
        </template>

        <button
          v-if="infoButtonPos === 'tabs'"
          @click="showInfo = !showInfo"
          class="shrink-0 rounded-full border border-gray-300 bg-white w-8 h-8 flex items-center justify-center text-xs font-bold text-gray-500 hover:bg-gray-50"
          :class="{ 'bg-blue-50 border-blue-300 text-blue-600': showInfo }"
          :aria-expanded="showInfo ? 'true' : 'false'"
          title="Show help"
        >?</button>
      </div>
    </div>

    <div v-if="claimSelectionError" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      {{ claimSelectionError }}
    </div>

    <!-- Fade gradient below sticky header -->
    <div class="absolute bottom-0 left-0 right-0 translate-y-full pointer-events-none" aria-hidden="true">
      <div class="h-2 bg-white pointer-events-none" /><!-- transparent gap -->
      <div class="h-4 bg-gradient-to-b from-white to-transparent pointer-events-none" />
    </div>
    </div><!-- /sticky header -->

    <!-- ── Scrollable content ── -->
    <div class="px-4 pt-4 pb-4 space-y-3 sm:space-y-4">
    <!-- ════════════════════════════════
         Arguments view
         ════════════════════════════════ -->
    <template v-if="viewMode === 'arguments'">
      <!-- Meta -->
      <div class="hidden sm:flex items-center gap-2 text-xs text-gray-600">
        <!-- Stance selector: stats-left -->
        <template v-if="stancePosition === 'stats-left'">
          <span class="text-gray-500">Stance:</span>
          <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Pro &amp; Con</option>
            <option value="pro">Pro</option>
            <option value="con">Con</option>
          </select>
        </template>
        <template v-if="argResp">
        <span class="hidden sm:inline px-2 py-1 rounded-full bg-gray-100 border">{{ argResp.total }} Arguments</span>
        <template v-if="searchStance === 'all' && argProResp && argConResp">
          <span class="hidden sm:inline px-2 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-700">{{ argProResp.total ?? 0 }} pro</span>
          <span class="hidden sm:inline px-2 py-1 rounded-full bg-[#3A91A5]/10 border border-[#3A91A5]/30 text-[#3A91A5]">{{ argConResp.total ?? 0 }} con</span>
        </template>
        <span class="hidden sm:inline px-2 py-1 rounded-full bg-gray-100 border">{{ argResp.took_ms ?? "—" }} ms</span>
        </template>
        <template v-if="sortPosition === 'stats-left' || sortPosition === 'stats-right'">
          <span class="text-gray-500" :class="{ 'ml-auto': sortPosition === 'stats-right' && stancePosition !== 'stats-right' }">Sort By:</span>
          <select v-model="searchSort" :disabled="!searchedWithTerms" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
            <option value="quality">Argument Quality</option>
            <option value="relevance">Search Relevance</option>
          </select>
        </template>
        <template v-if="stancePosition === 'stats-right'">
          <span class="text-gray-500 ml-auto">Stance:</span>
          <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Pro &amp; Con</option>
            <option value="pro">Pro</option>
            <option value="con">Con</option>
          </select>
        </template>
      </div>

      <!-- two-column -->
      <div v-if="searchStance === 'all'" class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <div ref="proColumnRef" class="space-y-4">
          <h2 class="text-purple-700 font-semibold">Pro-Arguments</h2>
          <HitCard v-for="hit in groupedArguments.pro" :key="hit.id" :hit="hit" :sorted-by="effectiveSort" :flat-info="flatInfo" @navigate-to-keypoint="navigateToKeypoint" />
          <!-- Mobile-only per-stance load more -->
          <div v-if="argResp && !argErr && argHasMoreForStance('pro')" class="sm:hidden flex justify-center">
            <button
              @click="argLoadMoreForStance('pro')"
              :disabled="argLoading"
              class="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ argLoading ? 'Loading…' : 'Load more' }}
            </button>
          </div>
        </div>
        <div ref="conColumnRef" class="space-y-4">
          <h2 class="text-[#3A91A5] font-semibold">Con-Arguments</h2>
          <HitCard v-for="hit in groupedArguments.con" :key="hit.id" :hit="hit" :sorted-by="effectiveSort" :flat-info="flatInfo" @navigate-to-keypoint="navigateToKeypoint" />
          <!-- Mobile-only per-stance load more -->
          <div v-if="argResp && !argErr && argHasMoreForStance('con')" class="sm:hidden flex justify-center">
            <button
              @click="argLoadMoreForStance('con')"
              :disabled="argLoading"
              class="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ argLoading ? 'Loading…' : 'Load more' }}
            </button>
          </div>
        </div>
      </div>

      <!-- single-column -->
      <div v-else class="grid grid-cols-1 gap-4">
        <HitCard v-for="hit in retrievedArguments" :key="hit.id" :hit="hit" :sorted-by="effectiveSort" :flat-info="flatInfo" @navigate-to-keypoint="navigateToKeypoint" />
      </div>

      <!-- Unified load more: hidden on mobile when both stances shown -->
      <div v-if="argResp && !argErr" class="flex justify-center" :class="{ 'hidden sm:flex': searchStance === 'all' }">
        <button
          v-if="hasMore"
          @click="loadMore"
          :disabled="argLoading"
          class="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ argLoading ? 'Loading…' : 'Load more' }}
        </button>
      </div>

      <!-- Error -->
      <div v-if="argErr" class="text-sm text-red-600 font-mono whitespace-pre-wrap">
        {{ pretty(argErr) }}
      </div>
    </template>

    <!-- ════════════════════════════════
         Keypoint view
         ════════════════════════════════ -->
    <template v-if="viewMode === 'keypoints'">
      <!-- Meta -->
      <div class="hidden sm:flex items-center gap-2 text-xs text-gray-600">
        <!-- Stance selector: stats-left -->
        <template v-if="stancePosition === 'stats-left'">
          <span class="text-gray-500">Stance:</span>
          <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Pro &amp; Con</option>
            <option value="pro">Pro</option>
            <option value="con">Con</option>
          </select>
        </template>
        <template v-if="kpResp">
        <span class="hidden sm:inline px-2 py-1 rounded-full bg-gray-100 border">{{ kpResp.total_arguments ?? 0 }} Arguments</span>
        <template v-if="searchStance === 'all'">
          <span class="hidden sm:inline px-2 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-700">{{ kpProArgCount }} pro</span>
          <span class="hidden sm:inline px-2 py-1 rounded-full bg-[#3A91A5]/10 border border-[#3A91A5]/30 text-[#3A91A5]">{{ kpConArgCount }} con</span>
        </template>
        <span class="hidden sm:inline px-2 py-1 rounded-full bg-gray-100 border">{{ kpResp.took_ms ?? "—" }} ms</span>
        </template>
        <template v-if="sortPosition === 'stats-left' || sortPosition === 'stats-right'">
          <span class="text-gray-500" :class="{ 'ml-auto': sortPosition === 'stats-right' && stancePosition !== 'stats-right' }">Sort By:</span>
          <select v-model="searchSort" :disabled="!searchedWithTerms" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
            <option value="quality">Argument Quality</option>
            <option value="relevance">Search Relevance</option>
          </select>
        </template>
        <template v-if="stancePosition === 'stats-right'">
          <span class="text-gray-500 ml-auto">Stance:</span>
          <select v-model="searchStance" class="rounded-xl border border-gray-300 px-2 py-1 text-xs text-gray-600
                   focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Pro &amp; Con</option>
            <option value="pro">Pro</option>
            <option value="con">Con</option>
          </select>
        </template>
      </div>

      <!-- two-column keypoints -->
      <div v-if="searchStance === 'all'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="space-y-3">
          <h2 class="text-purple-700 font-semibold">Pro</h2>
          <KeypointCard
            v-for="kp in kpMergedProKeypoints"
            :key="kp.key_point"
            :data-keypoint="kp.key_point"
            data-stance="pro"
            :group="kp"
            :sorted-by="effectiveSort"
            :flat-info="flatInfo" :loading-more="kpIsGroupLoading(kp)"
            :greyed="kp.greyed"
            :expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] ?? false"
            stance-color="pro"
            @update:expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] = $event"
            @load-more="kpLoadMore"
          />
          <p v-if="kpMergedProKeypoints.length === 0 && kpResp" class="text-sm text-gray-400 italic">No pro key points found.</p>
        </div>
        <div class="space-y-3">
          <h2 class="text-[#3A91A5] font-semibold">Con</h2>
          <KeypointCard
            v-for="kp in kpMergedConKeypoints"
            :key="kp.key_point"
            :data-keypoint="kp.key_point"
            data-stance="con"
            :group="kp"
            :sorted-by="effectiveSort"
            :flat-info="flatInfo" :loading-more="kpIsGroupLoading(kp)"
            :greyed="kp.greyed"
            :expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] ?? false"
            stance-color="con"
            @update:expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] = $event"
            @load-more="kpLoadMore"
          />
          <p v-if="kpMergedConKeypoints.length === 0 && kpResp" class="text-sm text-gray-400 italic">No con key points found.</p>
        </div>
      </div>

      <!-- single-column keypoints -->
      <div v-else class="space-y-3">
        <KeypointCard
          v-for="kp in kpMergedKeypoints"
          :key="kp.key_point"
          :data-keypoint="kp.key_point"
          :data-stance="searchStance === 'pro' ? 'pro' : 'con'"
          :group="kp"
          :sorted-by="effectiveSort"
          :flat-info="flatInfo" :loading-more="kpIsGroupLoading(kp)"
          :greyed="kp.greyed"
          :expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] ?? false"
          :stance-color="searchStance === 'pro' ? 'pro' : 'con'"
          @update:expanded="expandedStates[`${kp.stance ?? kp.arguments[0]?.stance ?? ''}:${kp.key_point}`] = $event"
          @load-more="kpLoadMore"
        />
      </div>

      <!-- Error -->
      <div v-if="kpErr" class="text-sm text-red-600 font-mono whitespace-pre-wrap">
        {{ pretty(kpErr) }}
      </div>
    </template>
    </div><!-- /scrollable content -->

    <!-- Search button: claim bar (teleport) -->
    <Teleport v-if="searchButtonPos === 'upper'" to="#claim-bar-actions">
      <button
        @click="onSearch"
        :disabled="activeLoading"
        class="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white
              hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ activeLoading ? 'Searching…' : 'Search' }}
      </button>
    </Teleport>

    <!-- Info button: claim bar (teleport) -->
    <Teleport v-if="infoButtonPos === 'controls'" to="#claim-bar-actions">
      <button
        @click="showInfo = !showInfo"
        class="shrink-0 rounded-full border border-gray-300 bg-white w-8 h-8 flex items-center justify-center text-xs font-bold text-gray-500 hover:bg-gray-50"
        :class="{ 'bg-blue-50 border-blue-300 text-blue-600': showInfo }"
        :aria-expanded="showInfo ? 'true' : 'false'"
        title="Show help"
      >?</button>
    </Teleport>

    <!-- Info button: floating -->
    <button
      v-if="infoButtonPos === 'floating'"
      @click="showInfo = !showInfo"
      class="fixed bottom-4 right-4 z-50 shrink-0 rounded-full border border-gray-300 bg-white w-8 h-8 flex items-center justify-center text-xs font-bold text-gray-500 hover:bg-gray-50 shadow-md"
      :class="{ 'bg-blue-50 border-blue-300 text-blue-600': showInfo }"
      :aria-expanded="showInfo ? 'true' : 'false'"
      title="Show help"
    >?</button>
  </div>
</template>


<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue'
import HitCard from './HitCard.vue'
import KeypointCard from './KeypointCard.vue'
import { useArgumentSearch } from '@/composables/useArgumentSearch'
import { useKeypointSearch } from '@/composables/useKeypointSearch'
import { useQueryParam } from '@/composables/useQueryParam'

const props = defineProps<{
  claimId?: string | null
  stickyClaimBar?: boolean
  stickyHeaderOffset?: number
}>()

const emit = defineEmits<{
  (e: 'update:stickyClaimBar', value: boolean): void
}>()

const stickyClaimBarModel = computed<boolean>({
  get: () => props.stickyClaimBar ?? false,
  set: (v) => emit('update:stickyClaimBar', v),
})

const viewMode = useQueryParam<'arguments' | 'keypoints'>('view', 'keypoints')

/* ── Column refs for visual balancing ── */
const proColumnRef = ref<HTMLElement | null>(null)
const conColumnRef = ref<HTMLElement | null>(null)
const stickyHeaderRef = ref<HTMLElement | null>(null)

/* ── Argument search ── */

const {
  q: argQ,
  stance: argStance,
  sortField: argSortField,
  resp: argResp,
  proResp: argProResp,
  conResp: argConResp,
  err: argErr,
  loading: argLoading,
  retrievedArguments,
  groupedArguments,
  hasMore,
  doSearch: argDoSearch,
  loadMore: argLoadMore,
  loadMoreForStance: argLoadMoreForStance,
  hasMoreForStance: argHasMoreForStance,
  loadExtraForStance,
} = useArgumentSearch({
  claimId: () => props.claimId,
  limit: 3
})

/* ── Keypoint search ── */

const {
  q: kpQ,
  stance: kpStance,
  sortField: kpSortField,
  resp: kpResp,
  err: kpErr,
  loading: kpLoading,
  isGroupLoading: kpIsGroupLoading,
  proKeypoints: kpProKeypoints,
  conKeypoints: kpConKeypoints,
  mergedKeypoints: kpMergedKeypoints,
  mergedProKeypoints: kpMergedProKeypoints,
  mergedConKeypoints: kpMergedConKeypoints,
  doSearch: kpDoSearch,
  fetchBaseline: kpFetchBaseline,
  loadMoreForKeypoint: kpLoadMore,
} = useKeypointSearch({
  claimId: () => props.claimId,
  keypointLimit: 20,
})

/* ── Unified query/stance bindings — synced to URL ── */

const searchQuery = useQueryParam('q', '')
const searchStance = useQueryParam<'all' | 'pro' | 'con'>('stance', 'all')
const searchSort = useQueryParam<'relevance' | 'quality'>('sort', 'quality')

// Initialize composables from URL-restored values
argQ.value = searchQuery.value
kpQ.value = searchQuery.value
argStance.value = searchStance.value
kpStance.value = searchStance.value
argSortField.value = searchSort.value
kpSortField.value = searchSort.value

// Sync shared controls → both composables on future changes
watch(searchQuery, (v) => { argQ.value = v; kpQ.value = v })
watch(searchStance, (v) => {
  argStance.value = v
  kpStance.value = v
  // Re-run search if a search has already been performed
  if (hasSearched.value) onSearch()
})
watch(searchSort, (v) => {
  argSortField.value = v
  kpSortField.value = v
  if (hasSearched.value) onSearch()
})

// Tracks whether the last executed search had optional terms
const searchedWithTerms = ref(false)


const kpProArgCount = computed(() =>
  kpProKeypoints.value.reduce((sum, kp) => sum + kp.argument_count, 0)
)

const kpConArgCount = computed(() =>
  kpConKeypoints.value.reduce((sum, kp) => sum + kp.argument_count, 0)
)

// When no optional terms, always treat as quality sort (no relevance score)
const effectiveSort = computed(() =>
  searchedWithTerms.value ? searchSort.value : 'quality'
)

const activeLoading = computed(() => argLoading.value || kpLoading.value)

const showKeypointsTab = computed(() =>
  // Show tab if we haven't searched yet, or if keypoints were found
  !hasSearched.value || kpMergedKeypoints.value.length > 0
)


const showInfo = ref(false)
const isDevMode = new URLSearchParams(window.location.search).has('dev')
const devOptionsVisible = ref(true)
const flatInfo = ref(true)
const sortPosition = ref<'views-left' | 'views-right' | 'stats-left' | 'stats-right'>('views-right')
const stancePosition = ref<'views-left' | 'views-right' | 'stats-left' | 'stats-right'>('views-right')
const infoButtonPos = ref<'controls' | 'upper-right' | 'tabs' | 'floating'>('floating')
const searchButtonPos = ref<'upper' | 'lower'>('lower')
const claimSelectionError = ref<string | null>(null)

// Track whether a search has ever been performed
const hasSearchedOnce = ref(false)

// Track whether results currently exist (to gate auto-re-search on stance change)
const hasSearched = computed(() => argResp.value !== null || kpResp.value !== null || hasSearchedOnce.value)

/**
 * After arguments render, check if columns are unbalanced.
 * If the shorter column's gap is large enough to fit another card,
 * fetch one extra argument for that side.
 */
const balanceColumns = async () => {
  if (searchStance.value !== 'all') return

  await nextTick() // wait for DOM to update

  const proEl = proColumnRef.value
  const conEl = conColumnRef.value
  if (!proEl || !conEl) return

  const proH = proEl.scrollHeight
  const conH = conEl.scrollHeight
  const gap = Math.abs(proH - conH)

  console.log('[balanceColumns]', { proH, conH, gap })

  if (gap < 50) return // close enough, no balancing needed

  const shorterSide: 'pro' | 'con' = proH < conH ? 'pro' : 'con'
  const shorterEl = shorterSide === 'pro' ? proEl : conEl
  const shorterCount = shorterSide === 'pro'
    ? groupedArguments.value.pro.length
    : groupedArguments.value.con.length

  if (shorterCount === 0) return

  // Estimate avg card height for the shorter side
  const avgCardHeight = shorterEl.scrollHeight / shorterCount

  // Only add if the gap can fit roughly one more card
  if (gap < avgCardHeight * 0.6) return

  const extra = await loadExtraForStance(shorterSide)
  if (extra) {
    // Recurse once more in case still unbalanced
    await balanceColumns()
  }
}

const loadMore = async () => {
  await argLoadMore()
  await balanceColumns()
}

let lastSearchedClaimId: string | null = null

const onSearch = async () => {
  if (!props.claimId) {
    claimSelectionError.value = 'Claim must match one of the autocomplete suggestions. Please select a suggested claim first.'
    return
  }
  claimSelectionError.value = null

  if (lastSearchedClaimId !== null && lastSearchedClaimId !== props.claimId) {
    Object.keys(expandedStates).forEach(k => delete expandedStates[k])
  }
  lastSearchedClaimId = props.claimId

  searchedWithTerms.value = searchQuery.value.trim().length > 0
  if (!searchedWithTerms.value) searchSort.value = 'quality'

  // When searching with terms, ensure we have baseline keypoints for greyed-out display
  if (searchedWithTerms.value) {
    await kpFetchBaseline()
  }

  // Fire both searches in parallel
  await Promise.all([argDoSearch(), kpDoSearch()])

  hasSearchedOnce.value = true


  await balanceColumns()
}

watch(
  () => props.claimId,
  (claimId) => {
    if (claimId) claimSelectionError.value = null
  }
)

const expandedStates = reactive<Record<string, boolean>>({})

const savedScrollY: Record<'arguments' | 'keypoints', number> = { arguments: 0, keypoints: 0 }

const switchView = async (
  to: 'arguments' | 'keypoints',
  scrollToKeypoint?: string,
  scrollToStance?: string,
) => {
  savedScrollY[viewMode.value] = window.scrollY
  viewMode.value = to
  await nextTick()
  if (to === 'arguments') await balanceColumns()
  if (scrollToKeypoint) {
    // Wait an extra frame so layout has settled after the v-if swap
    await nextTick()
    await new Promise(requestAnimationFrame)
    const norm = (s: string | undefined) => (s ?? '').trim().toLowerCase()
    const wanted = norm(scrollToKeypoint)
    const allNodes = Array.from(document.querySelectorAll<HTMLElement>('[data-keypoint]'))
    const exact = allNodes.filter(node => node.dataset.keypoint === scrollToKeypoint)
    const fuzzy = exact.length ? exact : allNodes.filter(node => norm(node.dataset.keypoint) === wanted)
    const el =
      fuzzy.find(node => !scrollToStance || node.dataset.stance === scrollToStance) ??
      fuzzy[0]
    if (el) {
      const stickyHeight = (props.stickyHeaderOffset ?? 0) + (stickyHeaderRef.value?.offsetHeight ?? 0) + 8
      const top = el.getBoundingClientRect().top + window.scrollY - stickyHeight
      window.scrollTo({ top, behavior: 'smooth' })
    }
  } else {
    window.scrollTo({ top: savedScrollY[to], behavior: 'instant' })
  }
}

const navigateToKeypoint = (keyPoint: string, stance: string | undefined) => {
  // The argument-search and keypoint-search endpoints can disagree on casing
  // for the same keypoint. Resolve to the canonical string from kpMergedKeypoints
  // so expandedStates and the scroll target both line up with what's rendered.
  const wanted = keyPoint.trim().toLowerCase()
  const match = kpMergedKeypoints.value.find(kp => {
    if (kp.key_point.trim().toLowerCase() !== wanted) return false
    const kpStanceVal = kp.stance ?? kp.arguments[0]?.stance
    return !stance || !kpStanceVal || kpStanceVal === stance
  })
  const canonicalKeyPoint = match?.key_point ?? keyPoint
  const canonicalStance = match?.stance ?? match?.arguments[0]?.stance ?? stance
  expandedStates[`${canonicalStance ?? ''}:${canonicalKeyPoint}`] = true
  switchView('keypoints', canonicalKeyPoint, canonicalStance)
}

const pretty = (v: unknown) => JSON.stringify(v, null, 2)

defineExpose({ triggerSearch: onSearch })

</script>
